import { faUser } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { StarRating } from '@/components/reviews-hub/ui/star-rating';
import { VerifiedBadge } from '@/components/reviews-hub/ui/verified-badge';
import { Icon } from '@/components/ui/icon';
import { Paragraph } from '@/components/ui/typography';
import type { ReviewRecord } from '@/lib/blocks/collection/records';
import { richTextToPlainText } from '@/lib/rich-text';
import { cn } from '@/lib/utils';

export const REVIEW_CARD_PADDING_CLASS = {
  sm: 'px-4 py-3',
  md: 'px-5 py-4',
  lg: 'px-6 py-5',
} as const;

export function ReviewStars({ review, className }: { review: ReviewRecord; className?: string }) {
  if (!review.showStarRatingOnWebsite || !review.rating) return null;

  return <StarRating rating={review.rating} className={className} />;
}

export function ReviewAttribution({
  review,
  compact = false,
  showIcon = true,
  showName = true,
  className,
}: {
  review: ReviewRecord;
  compact?: boolean;
  showIcon?: boolean;
  showName?: boolean;
  className?: string;
}) {
  const name = review.reviewerName || 'Anonymous';

  if (!showIcon && !showName) return null;

  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      {showIcon ? (
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground',
            compact ? 'size-5' : 'size-6',
          )}
        >
          <Icon icon={faUser} className="size-2.5" />
        </div>
      ) : null}
      {showName ? (
        <div className="flex items-center gap-1">
          <Paragraph
            className={cn('m-0 font-bold text-slate-700', compact ? 'text-[12px]' : 'text-base')}
          >
            {name}
          </Paragraph>
          {review.verified ? <VerifiedBadge /> : null}
        </div>
      ) : null}
    </div>
  );
}

export function ReviewQuote({
  review,
  bold = false,
  className,
}: {
  review: ReviewRecord;
  bold?: boolean;
  className?: string;
}) {
  const plainText = richTextToPlainText(review.description);
  if (!plainText) return null;

  return (
    <p
      className={cn(
        'text-[14px] leading-5 text-slate-700',
        bold ? 'text-2xl leading-[30px] font-extrabold text-slate-900' : 'font-normal',
        className,
      )}
    >
      {plainText}
    </p>
  );
}
